import React from "react";
import {
  getItemWidthBreakpoints,
  Grid,
  GridContent,
  GridItem,
  GridLoadMore,
} from "@aleksundshantu/aunds-utils-react/primitives/grid";
import { Section } from "@/components/Section/Section";
import { useSublinesVisibility } from "@/hooks/useSublinesOverlinesVisibility";
import {
  HeadersOptionsType,
  TeamGridOptionsType,
} from "@/types/ModuleOptionsType";
import { Modul_Mitarbeiter_Grid } from "@/wp/types/acf-module-types";
import { useRouter } from "next/router";
import { TeamCard, TeamCardElementType } from "@/components/TeamCard/TeamCard";
import { OptionsSocialMediaType } from "@/types/OptionType";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import { columnSize } from "@/utils/layout/columnSize";
import buttonStyles from "../../components/Button/Button.module.scss";
import { fetchTeams } from "@/getData/fetchTeams";
import { HeadlineV2 } from "@/components/Headline/HeadlineV2";
import { mockTeamPost } from "@/ui/mock-data";

export type TeamGridModuleType = Modul_Mitarbeiter_Grid;

interface TeamGridProps {
  data: TeamGridModuleType;
  gridOptions: TeamGridOptionsType;
  socialMedia: OptionsSocialMediaType;
  teamheaderOptions: HeadersOptionsType["header_team"];
}

const TeamGrid = ({
  data,
  gridOptions,
  socialMedia,
  teamheaderOptions,
}: TeamGridProps) => {
  {
    /* jscpd:ignore-start */
  }

  const {
    background_color_class,
    headline_text_align_class,
    id,
    section_margin_class,
    section_padding_class,
    module_width,
    grid_columns,
    posts_per_page,
  } = data.layout;

  const { headline, headline_html_tag, overline, subline } =
    data.team_grid || {};

  const {
    show_overline_subline,
    columns,
    grid_options: { grid_navigation, grid_columns_choices },
  } = gridOptions ?? {};

  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline,
  );

  const { locale, defaultLocale } = useRouter();
  const fallbackItemWidth = {
    mobile: 1,
    tablet: 2,
    desktop: 3,
    widescreen: 4,
    fullhd: posts_per_page === 4 ? 4 : 5,
    ultrahd: posts_per_page === 4 ? 4 : posts_per_page === 5 ? 5 : 6,
  };
  const itemWidth = getItemWidthBreakpoints(
    grid_columns_choices,
    grid_columns,
    fallbackItemWidth,
  );

  {
    /* jscpd:ignore-start */
  }
  //! check if module is hidden
  const isHidden = checkModuleIsHidden(data.settings);
  if (isHidden) {
    return null;
  }
  {
    /* jscpd:ignore-end */
  }
  const GridJSX = (
    <Grid
      postsPerPage={posts_per_page}
      fetchItems={({ page, postsPerPage, offset }) => {
        return fetchTeams({
          page,
          postsPerPage,
          offset,
          locale,
          defaultLocale,
          module: data,
        });
      }}
      renderLoading
    >
      {(gridPosts, loading) => {
        return (
          <>
            <GridContent justifyContent="center" gap="2rem">
              {gridPosts.map((element, key) => {
                return (
                  <GridItem itemWidth={itemWidth.result} key={key}>
                    <TeamCard
                      skeleton={loading}
                      socialMedia={socialMedia}
                      team={
                        loading
                          ? mockTeamPost
                          : (element as TeamCardElementType)
                      }
                      gridOptions={gridOptions}
                      teamheaderOptions={teamheaderOptions}
                      headlineTag={headline_html_tag}
                    />
                  </GridItem>
                );
              })}
            </GridContent>
            <GridLoadMore
              grid_navigation={grid_navigation}
              buttonStyles={buttonStyles}
            />
          </>
        );
      }}
    </Grid>
  );

  return (
    <Section
      id={id}
      disableClip
      frameless={module_width === "frameless"}
      columnsClassName="is-centered is-multiline"
      className={`teams-grid h-100 ${background_color_class} ${section_padding_class} ${section_margin_class} `}
    >
      <div
        className={`column ${module_width === "narrow-frame" ? columnSize(columns) : "is-12"}`}
      >
        {headline && (
          <HeadlineV2
            headline_html_tag={headline_html_tag}
            headline={headline}
            extraClassName={headline_text_align_class}
            overline={showOverline ? overline : ""}
            subline={showSubline ? subline : ""}
            disableAnimation
          />
        )}
        {GridJSX}
      </div>
    </Section>
  );

  {
    /* jscpd:ignore-end */
  }
};

export default TeamGrid;
